import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '../../lib/api.js';
import { Table, type Column } from '../../components/ui/Table.js';
import { Dialog } from '../../components/ui/Dialog.js';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog.js';
import { StatusPill, type PillVariant } from '../../components/ui/StatusPill.js';
import { useToast } from '../../components/ui/Toast.js';
import {
  ProviderNotConfiguredCard,
  errMessage,
  isProviderNotConfigured,
  retryUnlessClientError,
} from './shared.js';
import './infrastructure.css';

interface DatabaseCluster {
  id: string;
  name: string;
  engine: string;
  version: string | null;
  status: string;
  region: string | null;
  size: string | null;
  numNodes: number;
  tags: string[];
  createdAt: string | null;
}

interface DoRegion {
  slug: string;
  name: string;
  available: boolean;
  sizes: string[];
}

const ENGINES = ['pg', 'mysql', 'redis'] as const;
type Engine = (typeof ENGINES)[number];

const ENGINE_LABELS: Record<Engine, string> = {
  pg: 'PostgreSQL',
  mysql: 'MySQL',
  redis: 'Redis',
};

function statusVariant(status: string): PillVariant {
  if (status === 'online') return 'ok';
  if (status === 'creating' || status === 'resizing' || status === 'migrating') return 'warn';
  if (status === 'error') return 'err';
  return 'neutral';
}

export function DatabasesPage() {
  const queryClient = useQueryClient();
  const toast = useToast();

  const databasesQuery = useQuery<DatabaseCluster[]>({
    queryKey: ['do', 'databases'],
    queryFn: () => apiFetch<DatabaseCluster[]>('/do/databases'),
    retry: retryUnlessClientError,
  });

  // ── create dialog ──
  const [createOpen, setCreateOpen] = useState(false);
  const [name, setName] = useState('');
  const [engine, setEngine] = useState<Engine>('pg');
  const [version, setVersion] = useState('');
  const [region, setRegion] = useState('');
  const [size, setSize] = useState('');
  const [numNodes, setNumNodes] = useState(1);

  const resetForm = () => {
    setName('');
    setEngine('pg');
    setVersion('');
    setRegion('');
    setSize('');
    setNumNodes(1);
  };

  const closeCreate = () => {
    setCreateOpen(false);
    resetForm();
  };

  const regionsQuery = useQuery<DoRegion[]>({
    queryKey: ['do', 'regions'],
    queryFn: () => apiFetch<DoRegion[]>('/do/regions'),
    enabled: createOpen,
    retry: retryUnlessClientError,
  });

  const createMut = useMutation({
    mutationFn: (body: {
      name: string;
      engine: Engine;
      version?: string;
      region: string;
      size: string;
      num_nodes: number;
    }) => apiFetch<DatabaseCluster>('/do/databases', { method: 'POST', body }),
    onSuccess: async (created) => {
      await queryClient.invalidateQueries({ queryKey: ['do', 'databases'] });
      closeCreate();
      toast.success(`Database cluster "${created.name}" is being created`);
    },
    onError: (err) => toast.error(errMessage(err, 'failed to create database cluster')),
  });

  const submitCreate = () => {
    const v = version.trim();
    createMut.mutate({
      name: name.trim(),
      engine,
      ...(v ? { version: v } : {}),
      region,
      size: size.trim(),
      num_nodes: numNodes,
    });
  };

  // ── delete ──
  const [deleteTarget, setDeleteTarget] = useState<DatabaseCluster | null>(null);
  const deleteMut = useMutation({
    mutationFn: (db: DatabaseCluster) =>
      apiFetch<{ ok: boolean }>(`/do/databases/${db.id}`, {
        method: 'DELETE',
        body: { confirmName: db.name },
      }),
    onSuccess: async (_res, db) => {
      await queryClient.invalidateQueries({ queryKey: ['do', 'databases'] });
      toast.success(`Database cluster "${db.name}" deleted`);
      setDeleteTarget(null);
    },
    onError: (err) => toast.error(errMessage(err, 'delete failed')),
  });

  const columns: Column<DatabaseCluster>[] = [
    { key: 'name', header: 'Name', render: (db) => <span className="infra-name">{db.name}</span> },
    {
      key: 'engine',
      header: 'Engine',
      render: (db) => (
        <span className="badge">
          {db.engine}
          {db.version ? ` ${db.version}` : ''}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (db) => <StatusPill variant={statusVariant(db.status)}>{db.status}</StatusPill>,
    },
    {
      key: 'region',
      header: 'Region',
      render: (db) => <span className="ui-cell-mute">{db.region ?? '—'}</span>,
    },
    {
      key: 'size',
      header: 'Size',
      render: (db) => <span className="mono ui-cell-mute">{db.size ?? '—'}</span>,
    },
    {
      key: 'nodes',
      header: 'Nodes',
      render: (db) => <span className="ui-cell-mute">{db.numNodes}</span>,
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      render: (db) => (
        <div className="ui-cell-actions">
          <button className="btn btn-ghost-danger btn-sm" onClick={() => setDeleteTarget(db)}>
            Delete
          </button>
        </div>
      ),
    },
  ];

  const canCreate =
    name.trim().length > 0 && region !== '' && size.trim().length > 0 && numNodes >= 1;

  if (isProviderNotConfigured(databasesQuery.error)) {
    return (
      <div>
        <div className="page-head">
          <div>
            <h1>Databases</h1>
            <p className="page-sub">DigitalOcean managed database clusters.</p>
          </div>
        </div>
        <ProviderNotConfiguredCard provider="DigitalOcean" message={databasesQuery.error.message} />
      </div>
    );
  }

  return (
    <div>
      <div className="page-head">
        <div>
          <h1>Databases</h1>
          <p className="page-sub">DigitalOcean managed database clusters.</p>
        </div>
        <button className="btn btn-amber" onClick={() => setCreateOpen(true)}>
          Create cluster
        </button>
      </div>

      <Table
        columns={columns}
        rows={databasesQuery.data ?? []}
        rowKey={(db) => db.id}
        empty={
          databasesQuery.isLoading
            ? 'Loading…'
            : databasesQuery.isError
              ? errMessage(databasesQuery.error, 'failed to load database clusters')
              : 'No database clusters yet. Create one to get started.'
        }
      />

      {/* create dialog */}
      <Dialog open={createOpen} onClose={closeCreate} title="Create database cluster" width={520}>
        <form
          className="dialog-form"
          onSubmit={(e) => {
            e.preventDefault();
            if (canCreate && !createMut.isPending) submitCreate();
          }}
        >
          <div className="field">
            <label htmlFor="db-name">Name</label>
            <input
              id="db-name"
              autoFocus
              maxLength={255}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="app-db"
            />
          </div>
          <div className="field-grid-2">
            <div className="field">
              <label htmlFor="db-engine">Engine</label>
              <select
                id="db-engine"
                value={engine}
                onChange={(e) => setEngine(e.target.value as Engine)}
              >
                {ENGINES.map((eng) => (
                  <option key={eng} value={eng}>
                    {ENGINE_LABELS[eng]} ({eng})
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <label htmlFor="db-version">Version (optional)</label>
              <input
                id="db-version"
                className="mono"
                maxLength={16}
                value={version}
                onChange={(e) => setVersion(e.target.value)}
                placeholder={engine === 'pg' ? '16' : engine === 'mysql' ? '8' : '7'}
              />
            </div>
          </div>
          <div className="field-grid-2">
            <div className="field">
              <label htmlFor="db-region">Region</label>
              <select id="db-region" value={region} onChange={(e) => setRegion(e.target.value)}>
                <option value="" disabled>
                  {regionsQuery.isLoading ? 'Loading…' : 'Select region'}
                </option>
                {(regionsQuery.data ?? [])
                  .filter((r) => r.available)
                  .map((r) => (
                    <option key={r.slug} value={r.slug}>
                      {r.name} ({r.slug})
                    </option>
                  ))}
              </select>
            </div>
            <div className="field">
              <label htmlFor="db-nodes">Nodes</label>
              <select
                id="db-nodes"
                value={numNodes}
                onChange={(e) => setNumNodes(Number(e.target.value))}
              >
                {[1, 2, 3].map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="field">
            <label htmlFor="db-size">Size slug</label>
            <input
              id="db-size"
              className="mono"
              maxLength={64}
              value={size}
              onChange={(e) => setSize(e.target.value)}
              placeholder="db-s-1vcpu-1gb"
            />
          </div>
          <div className="dialog-actions">
            <button type="button" className="btn btn-ghost" onClick={closeCreate}>
              Cancel
            </button>
            <button type="submit" className="btn btn-amber" disabled={!canCreate || createMut.isPending}>
              {createMut.isPending ? 'Creating…' : 'Create cluster'}
            </button>
          </div>
        </form>
      </Dialog>

      {/* delete confirm */}
      <ConfirmDialog
        open={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => deleteTarget && deleteMut.mutate(deleteTarget)}
        title="Destroy database cluster"
        targetName={deleteTarget?.name ?? ''}
        description="All databases and backups in this cluster are destroyed."
        confirmLabel="Destroy cluster"
        busy={deleteMut.isPending}
      />
    </div>
  );
}
